import React, {useEffect, useState} from "react";
import DashboardHeader from "./dashboard_header";
import SideMenu from "./dashboard_menu";
import DashboardFooter from "./dashboard_footer";
import Canvas from "./dahboard_canvas";
import localforage from "localforage";
import axios from "axios";
import Swal from "sweetalert2";
import Datatable from "./components/datatable";
import {Link, useSearchParams} from "react-router-dom";
import { useBaseUrl } from "../../utils/utils";
import EHRNav from "./components/EHRNav";

const ACCENT = '#0f766e';

const PatientRecords: React.FC = () => {
    const [searchValue, setSearchValue] = useState("");
    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [step, setStep] = useState<number>(1);
    const [recordCount, setRecordCount] = useState<number>(0);
    const [records, setRecords] = useState<any[]>([]);
    const base_url = useBaseUrl();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        document.title = "Electronic Health Records - Uzima";
        // Auto-search if patient_id is provided in URL
        const patientId = searchParams.get('patient_id');
        if (patientId) {
            setSearchValue(patientId);
            doSearch(patientId);
        }
    }, []);

    const doSearch = async (value?: string) => {
        const sv = value || searchValue;
        if (!sv) {
            await Swal.fire({ icon: "warning", title: "Input Required", text: "Please enter a patient admission number to search." });
            return;
        }
        setIsWorking(true);
        try {
            const clinic_id = await localforage.getItem("clinic_id");
            const url = `${base_url}/api/v1/medical-records/?search=${sv}&clinic_id=${clinic_id}`;
            const response = await axios.get(url, { headers: { 'Content-Type': 'application/json' } }).finally(() => setIsWorking(false));
            const {status, message, data} = response.data;
            if (status === "success") {
                setStep(2);
                if (!data || !Array.isArray(data)) { setRecordCount(0); setRecords([]); return; }
                setRecordCount(data.length);
                setRecords(data);
            } else {
                await Swal.fire({ icon: "warning", title: "Search Failed", text: message || "No results found." });
            }
        } catch (e: any) {
            setIsWorking(false);
            await Swal.fire({ icon: "error", title: "Connection Error", text: e.message || "Could not connect to the server." });
        }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-file-find me-2" style={{ color: ACCENT }}></i>EHR Records Center</h1>
                                <p>Search, create, and manage electronic health records</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Link to="/institution/patients" className="sc-btn sc-btn-ghost" style={{ textDecoration: 'none' }}>
                                    <i className="bx bx-arrow-back"></i> Patient Center
                                </Link>
                                <Link to="/institution/ehr/new" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}>
                                    <i className="bx bx-plus"></i> New Record
                                </Link>
                            </div>
                        </div>

                        <EHRNav />

                        {step === 1 && (
                            <>
                                {/* Search */}
                                <div className="sc-card" style={{ marginBottom: 24 }}>
                                    <div style={{ padding: '18px 24px', borderBottom: `2px solid ${ACCENT}`, background: 'rgba(15,118,110,0.03)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div className="sc-stat-icon teal" style={{ width: 32, height: 32, borderRadius: 8, marginBottom: 0 }}><i className="bx bx-search" style={{ fontSize: '1rem' }}></i></div>
                                            <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--uzima-shell-text)', fontSize: '0.92rem' }}>Quick Patient Record Search</h6>
                                        </div>
                                    </div>
                                    <div className="sc-card-body" style={{ padding: 24 }}>
                                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                            <div style={{ flex: '1 1 300px' }}>
                                                <div className="sc-search" style={{ marginBottom: 0 }}>
                                                    <i className="bx bx-user"></i>
                                                    <input type="text" placeholder="Enter patient admission number or name..." value={searchValue}
                                                        onChange={e => setSearchValue(e.target.value)}
                                                        onKeyDown={e => { if (e.key === 'Enter') doSearch(); }} />
                                                </div>
                                                <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)', marginTop: 6 }}>
                                                    <i className="bx bx-info-circle me-1"></i>Search by admission number, patient name, or diagnosis
                                                </div>
                                            </div>
                                            <button className="sc-btn sc-btn-primary" onClick={() => doSearch()} disabled={isWorking} style={{ minWidth: 140, height: 42 }}>
                                                {isWorking ? <><span className="spinner-border spinner-border-sm me-2"></span>Searching...</> : <><i className="bx bx-search me-1"></i>Search Records</>}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions Grid */}
                                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div className="sc-stat-icon teal" style={{ width: 28, height: 28, borderRadius: 7, marginBottom: 0 }}><i className="bx bx-layer" style={{ fontSize: '0.85rem' }}></i></div>
                                    <div>
                                        <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--uzima-shell-text)', fontSize: '0.88rem' }}>EHR Management</h6>
                                        <p style={{ margin: 0, fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)' }}>Comprehensive tools for electronic health records</p>
                                    </div>
                                </div>

                                <div className="sc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
                                    {[
                                        { title: 'Create Record', desc: 'Start a new EHR', icon: 'bx-plus-medical', link: '/institution/ehr/new', cls: 'teal' },
                                        { title: 'Browse Records', desc: 'View all records', icon: 'bx-folder-open', link: '/institution/ehr/list', cls: 'blue' },
                                        { title: 'Export Records', desc: 'Share with facilities', icon: 'bx-export', link: '/institution/ehr/share', cls: 'amber' },
                                        { title: 'Shared With Us', desc: 'Records from others', icon: 'bx-cloud-download', link: '/institution/ehr/shared', cls: 'violet' },
                                        { title: 'Sharing Activity', desc: 'Consent & audit logs', icon: 'bx-history', link: '/institution/ehr/activity', cls: 'rose' },
                                    ].map((item, i) => (
                                        <Link to={item.link} key={i} className="sc-card" style={{ textDecoration: 'none', display: 'block' }}>
                                            <div className="sc-card-body" style={{ textAlign: 'center', padding: '24px 16px' }}>
                                                <div className={`sc-stat-icon ${item.cls}`} style={{ margin: '0 auto 14px', width: 48, height: 48, borderRadius: 14 }}>
                                                    <i className={`bx ${item.icon}`} style={{ fontSize: '1.3rem' }}></i>
                                                </div>
                                                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--uzima-shell-text)', marginBottom: 4 }}>{item.title}</div>
                                                <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)' }}>{item.desc}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>

                                {/* HIPAA Notice */}
                                <div style={{ padding: '12px 20px', borderRadius: 12, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div className="sc-stat-icon amber" style={{ width: 32, height: 32, borderRadius: 8, marginBottom: 0, flexShrink: 0 }}><i className="bx bx-shield-quarter" style={{ fontSize: '1rem' }}></i></div>
                                    <div style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)' }}>
                                        <b style={{ color: '#d97706' }}>HIPAA Compliance</b> — All medical records are protected health information (PHI). Access is logged and audited. Only authorized personnel may view or modify records.
                                    </div>
                                </div>
                            </>
                        )}

                        {step === 2 && (
                            <>
                                {recordCount === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
                                        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
                                            <i className="bx bx-search-alt" style={{ fontSize: '2rem', color: '#f59e0b' }}></i>
                                        </div>
                                        <h5 style={{ fontWeight: 700, color: 'var(--uzima-shell-text)', marginBottom: 6 }}>No Records Found</h5>
                                        <p style={{ color: 'var(--uzima-shell-text-muted)', maxWidth: 400, margin: '0 auto 20px', fontSize: '0.86rem' }}>
                                            No electronic health records found for "<b>{searchValue}</b>"
                                        </p>
                                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <button className="sc-btn sc-btn-primary" onClick={() => setStep(1)}><i className="bx bx-search"></i> Try Another Search</button>
                                            <Link to="/institution/ehr/new" className="sc-btn sc-btn-outline" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> Create Record</Link>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="sc-card" style={{ marginBottom: 20 }}>
                                            <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--uzima-shell-border)', background: 'rgba(16,185,129,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div className="sc-stat-icon teal" style={{ width: 32, height: 32, borderRadius: 8, marginBottom: 0 }}><i className="bx bx-check-circle" style={{ fontSize: '1rem' }}></i></div>
                                                    <div>
                                                        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--uzima-shell-text)' }}>Search Results</span>
                                                        <span style={{ marginLeft: 10, padding: '2px 10px', borderRadius: 20, background: ACCENT, color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>{recordCount} found</span>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setStep(1)}><i className="bx bx-search"></i> New Search</button>
                                                    <Link to="/institution/ehr/new" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> Create Record</Link>
                                                </div>
                                            </div>
                                            <div className="sc-card-body" style={{ padding: 24 }}>
                                                <div style={{ marginBottom: 12, fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                    Search: <b style={{ color: 'var(--uzima-shell-text)' }}>{searchValue}</b> · {new Date().toLocaleTimeString()}
                                                </div>
                                                <Datatable data={records} count={recordCount} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <button className="sc-btn sc-btn-outline" onClick={() => setStep(1)}><i className="bx bx-home"></i> EHR Home</button>
                                            <Link to="/institution/ehr/list" className="sc-btn sc-btn-ghost" style={{ textDecoration: 'none' }}><i className="bx bx-folder-open"></i> Browse All</Link>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />
        </>
    );
};

export default PatientRecords;
